import { LitElement, css, html, nothing } from '@lit'

class NovaCardVideo extends LitElement {
  static properties = {
    videoObject: {
      type: Object,
      attribute: 'video-object',
    },
    autoplay: {
      type: Boolean,
    },
    loop: {
      type: Boolean,
    },
    description: {
      type: String,
    },
  }

  get videoElement() {
    return this.renderRoot?.querySelector('video') ?? null
  }

  get posterImage() {
    const previewImage = this.videoObject?.preview_image
    if (typeof previewImage === 'string') return previewImage
    if (previewImage?.src) return previewImage.src
    return this.videoObject?.poster_image ?? null
  }

  constructor() {
    super()
    this.observer = null
    this.mobileViewportQuery = null
    this.hasHoverListeners = false
    this._hoverRoot = null
    this._pendingIntersection = null
    this._videoRetryInterval = null
  }

  _getHoverRoot() {
    return this.closest('.product-card__media') ?? this
  }

  _isHoverRootHovered() {
    const root = this._hoverRoot ?? this._getHoverRoot()
    return root.matches(':hover')
  }

  _nudgePlaybackAfterSourceChange = () => {
    if (!this.isConnected) return

    const video = this.videoElement
    if (!video) {
      requestAnimationFrame(() => {
        if (this.isConnected && this.videoElement) {
          this._nudgePlaybackAfterSourceChange()
        }
      })
      return
    }

    this._syncObserverState()

    if (this.mobileViewportQuery?.matches) {
      this.observer?.unobserve(this)
      this.observer?.observe(this)
      if (this._pendingIntersection) {
        this._applyIntersectionPlayback(video, this._pendingIntersection)
      }
    } else if (this.autoplay || this._isHoverRootHovered()) {
      this._playVideo(video)
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('videoObject')) {
      // Browsers keep the previously selected media after <source> children
      // change unless load() is called. play() alone is not enough.
      requestAnimationFrame(() => {
        const video = this.videoElement
        if (video) {
          video.load()
        }
        this._nudgePlaybackAfterSourceChange()
      })
    }
  }

  _playVideo(video) {
    if (!video) return

    // Content `muted` maps to defaultMuted; WebKit often still needs the IDL
    // property set before non-gesture play() will succeed.
    video.muted = true

    const playback = video.play?.()
    if (playback?.catch) {
      playback.catch(() => {})
    }
  }

  _applyIntersectionPlayback(video, isIntersecting) {
    if (isIntersecting) {
      this._playVideo(video)
    } else {
      video.pause()
    }
  }

  _startVideoRetry() {
    if (this._videoRetryInterval) return

    this._videoRetryInterval = setInterval(() => {
      if (!this.isConnected) {
        this._stopVideoRetry()
        return
      }

      const video = this.videoElement
      if (!video) return

      this._stopVideoRetry()

      if (this._pendingIntersection !== null) {
        this._applyIntersectionPlayback(video, this._pendingIntersection)
      }
    }, 100)
  }

  _stopVideoRetry() {
    if (!this._videoRetryInterval) return
    clearInterval(this._videoRetryInterval)
    this._videoRetryInterval = null
  }

  _addHoverListeners() {
    if (this.autoplay || this.hasHoverListeners) return

    // Bind to the product-card media frame so overlays (quick shop, wishlist)
    // that reveal the video also keep playback running.
    const root = this._getHoverRoot()
    root.addEventListener('mouseenter', this._handleMouseover)
    root.addEventListener('mouseleave', this._handleMouseout)
    this._hoverRoot = root
    this.hasHoverListeners = true
  }

  _removeHoverListeners() {
    if (!this.hasHoverListeners) return

    const root = this._hoverRoot ?? this
    root.removeEventListener('mouseenter', this._handleMouseover)
    root.removeEventListener('mouseleave', this._handleMouseout)
    this._hoverRoot = null
    this.hasHoverListeners = false
  }

  _createObserver() {
    if (this.observer) return

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this._pendingIntersection = entry.isIntersecting

        const video = entry.target.renderRoot?.querySelector('video')
        if (!video) {
          if (entry.isIntersecting) {
            this._startVideoRetry()
          }
          return
        }

        this._stopVideoRetry()
        this._applyIntersectionPlayback(video, entry.isIntersecting)
      })
    }, {
      root: null,
      rootMargin: '-10% 0px -10% 0px',
      threshold: 0.5,
    })
  }

  _syncObserverState = () => {
    if (!this.mobileViewportQuery?.matches) {
      this.observer?.disconnect()
      this._stopVideoRetry()
      this._pendingIntersection = null
      if (this.autoplay) {
        this._removeHoverListeners()
        this._playVideo(this.videoElement)
      } else {
        this._addHoverListeners()
        // mouseenter will not re-fire if the pointer is already over the media
        // root after a viewport change, so resume when already hovered.
        if (this._isHoverRootHovered()) {
          this._playVideo(this.videoElement)
        } else {
          this.videoElement?.pause()
        }
      }
      return
    }

    this._removeHoverListeners()
    this._createObserver()
    this.observer?.observe(this)
  }

  connectedCallback() {
    super.connectedCallback()

    this.mobileViewportQuery = window.matchMedia('(max-width: 1199px)')
    if (this.mobileViewportQuery.addEventListener) {
      this.mobileViewportQuery.addEventListener('change', this._syncObserverState)
    } else {
      this.mobileViewportQuery.addListener(this._syncObserverState)
    }
    this._syncObserverState()
  }

  _handleMouseover = () => {
    this._playVideo(this.videoElement)
  }

  _handleMouseout = () => {
    this.videoElement?.pause()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this.mobileViewportQuery?.removeEventListener) {
      this.mobileViewportQuery.removeEventListener('change', this._syncObserverState)
    } else {
      this.mobileViewportQuery?.removeListener(this._syncObserverState)
    }
    this.observer?.disconnect()
    this._stopVideoRetry()
    this._pendingIntersection = null
    this._removeHoverListeners()
  }

  render() {
    return html`
      <video
        playsinline
        ?autoplay=${this.autoplay}
        ?loop=${this.loop}
        preload="none"
        muted
        aria-label=${this.description}
        poster=${this.posterImage ?? nothing}
      >
        ${this.videoObject?.sources?.map(
          (source) =>
            html`<source src=${source.url} type=${source.mime_type}></source>`,
        )}
      </video>
    `
  }

  static styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      position: relative;
    }
    video {
      background: black;
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  `
}

window.customElements.define('nova-card-video', NovaCardVideo)
